import React, { useState, useEffect, useRef } from "react";
import { Clock } from "lucide-react";

interface OfferCountdownProps {
  startsAt?: number | null;
  expiresAt?: number | null;
  compact?: boolean;
  onExpire?: () => void;
  className?: string;
  showIcon?: boolean;
}

export const OfferCountdown: React.FC<OfferCountdownProps> = ({
  startsAt,
  expiresAt,
  onExpire,
  className = "",
  showIcon = true,
}) => {
  const [now, setNow] = useState<number>(Date.now());
  const expiredTriggeredRef = useRef<boolean>(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const targetStartsAt = startsAt && typeof startsAt === "number" && startsAt > 0 ? startsAt : null;
  const targetExpiresAt = expiresAt && typeof expiresAt === "number" && expiresAt > 0 ? expiresAt : null;

  const isFutureStart = targetStartsAt != null && targetStartsAt > now;
  const targetTime = isFutureStart ? targetStartsAt : targetExpiresAt;

  if (targetTime == null) {
    return null;
  }

  const totalSeconds = Math.max(0, Math.floor((targetTime - now) / 1000));

  if (totalSeconds <= 0) {
    if (!isFutureStart && targetExpiresAt != null && !expiredTriggeredRef.current) {
      expiredTriggeredRef.current = true;
      if (onExpire) {
        onExpire();
      }
    }
    return null;
  }

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, "0");
  const prefix = isFutureStart ? "Starts in" : "Ends in";

  const formattedTime =
    days > 0
      ? `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

  const fullTimeText = `Offer ${prefix.toLowerCase()} in ${
    days > 0 ? `${days} days, ` : ""
  }${hours > 0 ? `${hours} hours, ` : ""}${minutes} minutes and ${seconds} seconds`;

  return (
    <div
      className={`inline-flex h-5 items-center gap-0.5 whitespace-nowrap rounded-full border border-amber-300/80 bg-white/95 px-1 text-[8px] font-semibold leading-none text-amber-950 shadow-sm backdrop-blur-sm tabular-nums sm:h-6 sm:gap-1 sm:px-1.5 sm:text-[9px] ${className}`}
      aria-live="off"
      aria-label={fullTimeText}
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      {showIcon && (
        <Clock className="h-2 w-2 sm:h-2.5 sm:w-2.5 text-amber-700 shrink-0" />
      )}
      <span className="font-bold">{formattedTime}</span>
    </div>
  );
};

export default OfferCountdown;
